'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { FinderGroup, LocationGroup, MyGroup, MyInquiry } from '@/lib/types';
import { campusSvgMap } from '@/lib/types';
import { fetchGroup, fetchLocations, fetchMyGroups } from '@/lib/api';
import { SJ_TITLE } from '@/lib/seeJesus';
import { CampusIcon, CampusSvgProvider } from './CampusIcon';
import { GroupDetailsBody } from './GroupDetailsBody';
import { SheetHeader } from './SheetHeader';
import { SeeJesusCorner, SeeJesusFrame, SeeJesusSheet } from './SeeJesus';
import { JoinSheet, hasPendingJoin } from './JoinSheet';

interface GroupDetailsProps {
  apiBaseUrl: string;
  groupId: number | null;
  /** Where "Back to all groups" goes */
  finderUrl?: string;
  inquiryFormUrl?: string;
}

const DEFAULT_FINDER_URL = 'https://woodsidebible.org/group-finder/';
const DEFAULT_INQUIRY_FORM_URL = 'https://woodsidebible.org/group-finder/join/';

type Status = 'loading' | 'ready' | 'missing' | 'error';

/** The card around the page: white and rounded, or the See Jesus frame. */
function Shell({ sj, children }: { sj: boolean; children: React.ReactNode }) {
  if (sj) {
    return (
      <SeeJesusFrame
        className="shadow-sm"
        paneClassName=""
        corner={<SeeJesusCorner className="w-24 sm:w-28" />}
      >
        {children}
      </SeeJesusFrame>
    );
  }
  return (
    <article className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
      {children}
    </article>
  );
}

/**
 * The details page: everything the finder's sheet shows, at page scale,
 * plus the inquiry form. Members see the real address and roster.
 */
export function GroupDetails({
  apiBaseUrl,
  groupId,
  finderUrl,
  inquiryFormUrl,
}: GroupDetailsProps) {
  const [group, setGroup] = useState<FinderGroup | null>(null);
  const [links, setLinks] = useState<{ details: string; inquiryForm: string } | null>(null);
  const [status, setStatus] = useState<Status>(groupId ? 'loading' : 'missing');
  const [locations, setLocations] = useState<LocationGroup[]>([]);
  const [mine, setMine] = useState<MyGroup | undefined>(undefined);
  const [inquiries, setInquiries] = useState<MyInquiry[]>([]);
  const [explain, setExplain] = useState(false);
  const [joinOpen, setJoinOpen] = useState(() => hasPendingJoin());

  useEffect(() => {
    if (!groupId) return;
    const controller = new AbortController();
    const { signal } = controller;
    setStatus('loading');

    fetchGroup(apiBaseUrl, groupId, signal)
      .then((data) => {
        if (!data) {
          setStatus('missing');
          return;
        }
        setGroup(data.group);
        setLinks(data.links);
        setStatus('ready');
      })
      .catch((err) => {
        if (signal.aborted) return;
        console.error('Group details failed to load:', err);
        setStatus('error');
      });

    fetchLocations(apiBaseUrl, signal)
      .then(setLocations)
      .catch(() => {
        /* campus icons are decoration */
      });

    fetchMyGroups(apiBaseUrl, signal).then((data) => {
      if (signal.aborted || !data) return;
      setMine(data.groups.find((g) => g.id === groupId));
      setInquiries((data.inquiries ?? []).filter((i) => i.groupId === groupId));
    });

    return () => controller.abort();
  }, [apiBaseUrl, groupId]);

  const refreshMine = useCallback(() => {
    if (!groupId) return;
    void fetchMyGroups(apiBaseUrl).then((data) => {
      if (!data) return;
      setMine(data.groups.find((g) => g.id === groupId));
      setInquiries((data.inquiries ?? []).filter((i) => i.groupId === groupId));
    });
  }, [apiBaseUrl, groupId]);

  const svgMap = useMemo(() => campusSvgMap(locations), [locations]);
  const sj = Boolean(group?.seeJesus);
  const backUrl = finderUrl || DEFAULT_FINDER_URL;
  const helpUrl = inquiryFormUrl || links?.inquiryForm || DEFAULT_INQUIRY_FORM_URL;

  if (status === 'missing' || status === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-200 bg-white px-6 py-14 text-center">
        <p className="text-lg font-bold text-[#1c2b39]">
          {status === 'missing'
            ? "We couldn't find that group."
            : "We couldn't load this group right now."}
        </p>
        <p className="max-w-md text-sm text-gray-600">
          {status === 'missing'
            ? 'It may have wrapped up for the season. There are plenty of others with room.'
            : "Please try again in a moment. If you'd rather talk to a person, we're happy to help."}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <a
            href={backUrl}
            className="inline-flex items-center gap-1 rounded-full bg-[#299c8f] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
          >
            Browse all groups
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </a>
          <button
            type="button"
            onClick={() => setJoinOpen(true)}
            className="inline-flex items-center gap-1 rounded-full bg-[#1c2b39] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
          >
            Help me find a group
          </button>
        </div>
        <JoinSheet
          open={joinOpen}
          onClose={() => setJoinOpen(false)}
          apiBaseUrl={apiBaseUrl}
          locations={locations}
        />
      </div>
    );
  }

  if (status === 'loading' || !group) {
    return (
      <div className="flex flex-col gap-5" aria-busy="true">
        <div className="h-5 w-40 animate-pulse rounded-full bg-gray-200" />
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <div className="h-40 animate-pulse bg-gray-200" />
          <div
            className={`grid gap-8 p-6 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] sm:p-8 ${sj ? 'sj-type text-[#f6efe9]' : ''}`}
          >
            <div className="flex flex-col gap-4">
              <div className="h-4 w-32 animate-pulse rounded-full bg-gray-200" />
              <div className="h-4 w-40 animate-pulse rounded-full bg-gray-200" />
              <div className="h-4 w-28 animate-pulse rounded-full bg-gray-200" />
            </div>
            <div className="flex flex-col gap-3">
              <div className="h-4 w-full animate-pulse rounded-full bg-gray-200" />
              <div className="h-4 w-11/12 animate-pulse rounded-full bg-gray-200" />
              <div className="h-4 w-3/4 animate-pulse rounded-full bg-gray-200" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CampusSvgProvider value={svgMap}>
      <SeeJesusSheet open={explain} onClose={() => setExplain(false)} />
      <JoinSheet
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        apiBaseUrl={apiBaseUrl}
        locations={locations}
        prefill={{ campusId: group.campusId, answers: {} }}
      />
      <div className="flex flex-col gap-5 text-[#1c2b39]">
        <a
          href={backUrl}
          className="inline-flex w-fit items-center gap-1 text-sm font-semibold text-gray-500 transition-colors hover:text-[#1c2b39]"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
          All groups
        </a>

        <Shell sj={sj}>
          <SheetHeader
            title={group.title}
            icon={<CampusIcon congregationId={group.campusId} className="h-8 w-8" />}
            className={`px-6 py-7 sm:px-8 sm:py-9 ${group.seeJesus ? 'sj-type' : ''}`}
            background={group.seeJesus ? null : undefined}
            titleClassName={group.seeJesus ? `${SJ_TITLE} pr-20 sm:pr-28` : ''}
          />

          <GroupDetailsBody
            apiBaseUrl={apiBaseUrl}
            group={group}
            mine={mine}
            inquiries={inquiries}
            onInquired={refreshMine}
            onHelp={() => setJoinOpen(true)}
            finderUrl={backUrl}
            inquiryFormUrl={helpUrl}
            onExplainSeeJesus={() => setExplain(true)}
          />
        </Shell>
      </div>
    </CampusSvgProvider>
  );
}
